import { Camera as CameraPlugin, CameraSource, CameraResultType } from "@capacitor/camera";

/**
 * @typedef {Object.<string, any>} CameraOptions
 * @property {Boolean} allowEditing - Whether to allow the user to crop or make small edits (platform specific)
 * @property {Boolean} correctOrientation - Whether to automatically rotate the image "up" to correct for orientation in portrait mode Default: true
 * @property {CameraDirection} direction - iOS only: The default camera direction. By default the rear camera. Default: CameraDirection.Rear
 * @property {Number} height - The height of the saved image
 * @property {Any} presentationStyle - iOS only: The presentation style of the Camera. Defaults to fullscreen.
 * @property {Number} quality - The quality of image to return as JPEG, from 0-100
 * @property {CameraResultType} resultType - How the data should be returned. Currently, only 'Base64', 'DataUrl' or 'Uri' is supported
 * @property {Boolean} saveToGallery - Whether to save the photo to the gallery/photostream
 * @property {CameraSource} source - The source to get the photo from. By default this prompts the user to select either the photo album or take a photo. Default: CameraSource.Prompt
 * @property {Number} width - The width of the saved image
 */

/**
 * Wrapper around Capacitor plugin camera
 * https://capacitor.ionicframework.com/docs/apis/camera/
 */
export class Camera {
  /**
   * Returns the Capacitor plugin instance
   * @readonly
   * @static
   */
  static get plugin() {
    return CameraPlugin;
  }

  /**
   * Default config suited for core usage
   * @readonly
   */
  static get defaultOptions() {
    return {
      // Whether to allow the user to crop or make small edits (platform specific)
      allowEditing: false,
      // The quality of image to return as JPEG, from 0-100
      quality: 100,
      // How the data should be returned. Currently, only 'Base64', 'DataUrl' or 'Uri' is supported
      resultType: CameraResultType.Base64,
      // Whether to save the photo to the gallery/photostream
      saveToGallery: false,
    };
  }

  /**
   * Will default to prompt on compatible device
   * https://capacitor.ionicframework.com/docs/apis/camera#type-288
   * @param {CameraOptions} cameraOptions
   */
  static getPhoto(cameraOptions) {
    const options = Object.assign(
      { source: CameraSource.Prompt }, // default to prompt
      Camera.defaultOptions,
      cameraOptions,
    );

    return Camera.plugin.getPhoto(options);
  }

  /**
   * Will default to open Camera on compatible device
   * https://capacitor.ionicframework.com/docs/apis/camera#type-288
   * @param {CameraOptions} cameraOptions
   */
  static getPhotoFromCamera(cameraOptions) {
    const options = Object.assign(
      { source: CameraSource.Camera }, // default to camera
      Camera.defaultOptions,
      cameraOptions,
    );

    return Camera.plugin.getPhoto(options);
  }

  /**
   * Will default to open Gallery on compatible device
   * https://capacitor.ionicframework.com/docs/apis/camera#type-288
   * @param {CameraOptions} cameraOptions
   */
  static getPhotoFromGallery(cameraOptions) {
    const options = Object.assign(
      { source: CameraSource.Photos }, // default to gallery
      Camera.defaultOptions,
      cameraOptions,
    );

    return Camera.plugin.getPhoto(options);
  }

  /**
   * Generate base64 from file input
   * @param {File} file
   * @returns {Base64}
   */
  static async getBase64FromFile(file = new File()) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      // my file is not a Blob, it's a File
      // i need to convert it to a Blob
      const blob = new Blob([file], { type: "image/jpeg" });
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Some browsers ignore the trailing and white spaces in Base64 inputs, others don't
   * That can create an issue when we try to decode the Base64 on mobile or Safari
   * This function prevents that without loss
   * @param {Base64} Base64 - the Base64 image we want to sanitize
   * @returns {Base64} - sanitized Base64
   */
  static sanitizeBase64(base64Input = "") {
    return base64Input.replace(/\s/g, "");
  }
}
